import type { LandIqDataRunCommandProgress, LandIqDataRunTimings } from '@immo/api-contract';

/** LandIQ writes a snapshot at least every ten minutes while a command makes progress (ADR data-runs/0027). */
export const PROGRESS_STALE_AFTER_MS = 20 * 60_000;

export type RunStage = 'queued' | 'command' | 'import';

const STAGES: ReadonlyArray<RunStage> = ['queued', 'command', 'import'];

/** done/total in [0, 1]; null when LandIQ does not know the total, so no bar is drawn for a guess. */
export function progressShare(progress: LandIqDataRunCommandProgress): number | null {
  if (progress.total === null || progress.total === 0) return null;

  return Math.min(1, progress.done / progress.total);
}

/** Whole percent done, rounded down and capped at 100, so a run never reads finished before it is; null without a total. */
export function progressPercent(progress: LandIqDataRunCommandProgress): number | null {
  const share = progressShare(progress);

  return share === null ? null : Math.floor(share * 100);
}

/** A running run whose latest snapshot is older than 20 min. A finished run is never stale. */
export function isProgressStale(progress: LandIqDataRunCommandProgress, running: boolean, now: number): boolean {
  return running && now - Date.parse(progress.updatedAt) > PROGRESS_STALE_AFTER_MS;
}

/** The stage times LandIQ knows, in stage order; a stage it has no moment for is left out. */
export function knownStageTimes(timings: LandIqDataRunTimings): ReadonlyArray<{ stage: RunStage; ms: number }> {
  const byStage: Record<RunStage, number | null> = {
    queued: timings.queuedMs,
    command: timings.commandMs,
    import: timings.importMs,
  };

  return STAGES.flatMap((stage) => {
    const ms = byStage[stage];

    return ms === null ? [] : [{ stage, ms }];
  });
}
